import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BoltIcon from "@mui/icons-material/Bolt";
import BugReportIcon from "@mui/icons-material/BugReport";
import RemoveIcon from "@mui/icons-material/Remove";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useTheme } from "@mui/material/styles";
import API from "../utils/api";
import useAuth from "../hooks/useAuth";
import { tError, tSuccess } from "../utils/toast";
import { userIsLegacyDefaultOrganization } from "../utils/legacyDefaultOrganization";

/** Release cards: icon, tint, and accent (bullets) per section. */
const CHANGELOG_SECTION_META = {
  added: {
    label: "Added",
    Icon: AddIcon,
    color: "#10B981",
    bg: { light: "rgba(16, 185, 129, 0.14)", dark: "rgba(16, 185, 129, 0.22)" },
  },
  improved: {
    label: "Improved",
    Icon: BoltIcon,
    color: "#3B82F6",
    bg: { light: "rgba(59, 130, 246, 0.14)", dark: "rgba(59, 130, 246, 0.22)" },
  },
  fixed: {
    label: "Fixed",
    Icon: BugReportIcon,
    color: "#F59E0B",
    bg: { light: "rgba(245, 158, 11, 0.16)", dark: "rgba(245, 158, 11, 0.24)" },
  },
  removed: {
    label: "Removed",
    Icon: RemoveIcon,
    color: "#EF4444",
    bg: { light: "rgba(239, 68, 68, 0.14)", dark: "rgba(239, 68, 68, 0.22)" },
  },
};

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

const EMPTY_FORM = {
  id: null,
  version: "",
  name: "",
  summary: "",
  status: "draft",
  release_date: new Date().toISOString().slice(0, 10),
  sections: { added: [""], improved: [""], fixed: [""], removed: [""] },
};

const normalizeSections = (sections = {}) => {
  const out = {};
  for (const key of ["added", "improved", "fixed", "removed"]) {
    const arr = Array.isArray(sections?.[key]) ? sections[key] : [];
    out[key] = arr.map((v) => String(v || "").trim()).filter(Boolean);
  }
  return out;
};

function SectionEditor({ label, value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Stack spacing={1}>
        {items.map((item, idx) => (
          <TextField
            key={`${label}-${idx}`}
            size="small"
            value={item}
            onChange={(e) => {
              const next = [...items];
              next[idx] = e.target.value;
              onChange(next);
            }}
            placeholder={`Add ${label.toLowerCase()} item`}
            fullWidth
          />
        ))}
        <Button
          variant="outlined"
          size="small"
          onClick={() => onChange([...items, ""])}
          sx={{ alignSelf: "flex-start", textTransform: "none" }}
        >
          + Add item
        </Button>
      </Stack>
    </Box>
  );
}

export default function ChangelogPage() {
  const { role, user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const canManage =
    !!user?.is_superuser ||
    (role === "Admin" && userIsLegacyDefaultOrganization(user));
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState(canManage ? "all" : "published");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    setTab(canManage ? "all" : "published");
  }, [canManage]);

  const fetchReleases = async () => {
    setLoading(true);
    try {
      const res = await API.get("/changelog/releases/");
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      tError("Failed to load changelog releases.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReleases();
  }, []);

  const filteredRows = useMemo(() => {
    if (!canManage) return rows.filter((r) => r.status === "published");
    if (tab === "all") return rows;
    return rows.filter((r) => r.status === tab);
  }, [rows, tab, canManage]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      id: row.id,
      version: row.version || "",
      name: row.name || "",
      summary: row.summary || "",
      status: row.status || "draft",
      release_date: row.release_date || new Date().toISOString().slice(0, 10),
      sections: {
        added: row.sections?.added?.length ? row.sections.added : [""],
        improved: row.sections?.improved?.length ? row.sections.improved : [""],
        fixed: row.sections?.fixed?.length ? row.sections.fixed : [""],
        removed: row.sections?.removed?.length ? row.sections.removed : [""],
      },
    });
    setDialogOpen(true);
  };

  const saveForm = async () => {
    if (!form.version.trim() || !form.name.trim()) {
      tError("Version and release name are required.");
      return;
    }
    const payload = {
      version: form.version.trim(),
      name: form.name.trim(),
      summary: form.summary.trim(),
      status: form.status,
      release_date: form.release_date,
      sections: normalizeSections(form.sections),
    };

    try {
      setSaving(true);
      if (form.id) {
        await API.patch(`/changelog/releases/${form.id}/`, payload);
        tSuccess("Release updated.");
      } else {
        await API.post("/changelog/releases/", payload);
        tSuccess("Release created.");
      }
      window.dispatchEvent(new CustomEvent("whatsNewUpdated"));
      setDialogOpen(false);
      await fetchReleases();
    } catch (err) {
      console.error(err);
      tError(err?.response?.data?.detail || "Failed to save release.");
    } finally {
      setSaving(false);
    }
  };

  const publishRelease = async (id) => {
    try {
      await API.post(`/changelog/releases/${id}/publish/`);
      window.dispatchEvent(new CustomEvent("whatsNewUpdated"));
      tSuccess("Release published.");
      await fetchReleases();
    } catch (err) {
      console.error(err);
      tError(err?.response?.data?.detail || "Failed to publish release.");
    }
  };

  const deleteRelease = async (id) => {
    try {
      await API.delete(`/changelog/releases/${id}/`);
      tSuccess("Release deleted.");
      await fetchReleases();
    } catch (err) {
      console.error(err);
      tError(err?.response?.data?.detail || "Failed to delete release.");
    }
  };

  return (
    <Box sx={{ p: 3, px: { xs: 1, sm: 3 } }}>
      <Box sx={{ maxWidth: 1440, mx: "auto", width: "100%" }}>
        <Card sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(25,118,210,0.08)",
                }}
              >
                <AutoAwesomeIcon color="primary" />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Changelog
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {canManage
                    ? `Product updates and features release notes (${rows.length} ${rows.length === 1 ? "release" : "releases"})`
                    : `Latest published product updates (${filteredRows.length} ${filteredRows.length === 1 ? "release" : "releases"})`}
                </Typography>
              </Box>
            </Stack>
            {canManage && (
              <Button
                variant="contained"
                onClick={openCreate}
                fullWidth={isMobile}
                sx={{
                  borderRadius: "14px",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  letterSpacing: "-0.01em",
                  lineHeight: 1.4,
                  padding: "8px 16px",
                  whiteSpace: "nowrap",
                  width: { xs: "100%", md: "auto" },
                  mt: { xs: 2, md: 0 },
                }}
              >
                + New Release
              </Button>
            )}
          </Stack>
        </Card>

        {canManage && (
          <Card
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 3,
              boxShadow: "0 4px 6px rgba(0,0,0,0.08)",
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: "block" }}>
              Status
            </Typography>
            <ToggleButtonGroup
              value={tab}
              exclusive
              onChange={(e, newValue) => {
                if (newValue != null) setTab(newValue);
              }}
              size="small"
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="published">Published</ToggleButton>
              <ToggleButton value="draft">Drafts</ToggleButton>
            </ToggleButtonGroup>
          </Card>
        )}

        <Card
          data-tour="changelog-releases"
          sx={{
            borderRadius: 3,
            boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
            overflow: "visible",
          }}
        >
          <Box sx={{ px: 2, py: 2 }}>
            {loading ? (
              <Typography color="text.secondary">Loading changelog...</Typography>
            ) : filteredRows.length === 0 ? (
              <Typography color="text.secondary">No releases found.</Typography>
            ) : (
              <Stack spacing={2}>
                {filteredRows.map((row) => (
                  <Card key={row.id} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
                    <CardContent sx={{ "&:last-child": { pb: 2 } }}>
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={{ xs: 1.25, md: 0 }}
                        justifyContent="space-between"
                        alignItems={{ xs: "stretch", md: "flex-start" }}
                        sx={{ mb: 1, columnGap: 2 }}
                      >
                        <Box sx={{ flex: { md: "1 1 0" }, minWidth: 0, maxWidth: "100%" }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              lineHeight: 1.25,
                              wordBreak: "break-word",
                            }}
                          >
                            {row.version} - {row.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                            {formatDate(row.release_date)}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Chip
                            label={row.status === "published" ? "Published" : "Draft"}
                            color={row.status === "published" ? "success" : "default"}
                            size="small"
                          />
                          {canManage && (
                            <Button size="small" variant="outlined" onClick={() => openEdit(row)}>
                              Edit
                            </Button>
                          )}
                          {canManage && row.status !== "published" && (
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              onClick={() => publishRelease(row.id)}
                            >
                              Publish
                            </Button>
                          )}
                          {canManage && (
                            <Button size="small" variant="text" color="error" onClick={() => deleteRelease(row.id)}>
                              Delete
                            </Button>
                          )}
                        </Stack>
                      </Stack>

                      {row.summary ? (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {row.summary}
                        </Typography>
                      ) : null}

                      <Divider sx={{ my: 1 }} />
                      <Stack spacing={2.5}>
                        {["added", "improved", "fixed", "removed"].map((key) => {
                          const list = row.sections?.[key] || [];
                          if (!list.length) return null;
                          const meta = CHANGELOG_SECTION_META[key];
                          if (!meta) return null;
                          const { Icon, color, label, bg } = meta;
                          const iconBg = theme.palette.mode === "dark" ? bg.dark : bg.light;
                          const listGutter = 36 + 12;
                          return (
                            <Box key={`${row.id}-${key}`}>
                              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                                <Box
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    bgcolor: iconBg,
                                    color,
                                  }}
                                >
                                  <Icon sx={{ fontSize: 20 }} />
                                </Box>
                                <Typography variant="subtitle2" fontWeight={700}>
                                  {label}
                                </Typography>
                              </Stack>
                              <Stack
                                component="ul"
                                spacing={0.75}
                                sx={{
                                  m: 0,
                                  p: 0,
                                  listStyle: "none",
                                  pl: `${listGutter}px`,
                                }}
                              >
                                {list.map((item, idx) => (
                                  <Box
                                    component="li"
                                    key={`${row.id}-${key}-${idx}`}
                                    sx={{
                                      display: "flex",
                                      gap: 1.25,
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: "50%",
                                        bgcolor: color,
                                        flexShrink: 0,
                                        mt: "7px",
                                      }}
                                    />
                                    <Typography variant="body2" color="text.primary" sx={{ flex: 1, lineHeight: 1.5 }}>
                                      {item}
                                    </Typography>
                                  </Box>
                                ))}
                              </Stack>
                            </Box>
                          );
                        })}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Box>
        </Card>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{form.id ? "Edit Release" : "New Release"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Version"
                value={form.version}
                onChange={(e) => setForm((p) => ({ ...p, version: e.target.value }))}
                fullWidth
                size="small"
              />
              <TextField
                label="Release Name"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                fullWidth
                size="small"
              />
            </Stack>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Release Date"
                type="date"
                value={form.release_date}
                onChange={(e) => setForm((p) => ({ ...p, release_date: e.target.value }))}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                select
                label="Status"
                value={form.status}
                onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                fullWidth
                size="small"
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="published">Published</MenuItem>
              </TextField>
            </Stack>
            <TextField
              label="Summary"
              value={form.summary}
              onChange={(e) => setForm((p) => ({ ...p, summary: e.target.value }))}
              multiline
              rows={2}
              fullWidth
              size="small"
            />
            <SectionEditor
              label="Added"
              value={form.sections.added}
              onChange={(v) => setForm((p) => ({ ...p, sections: { ...p.sections, added: v } }))}
            />
            <SectionEditor
              label="Improved"
              value={form.sections.improved}
              onChange={(v) => setForm((p) => ({ ...p, sections: { ...p.sections, improved: v } }))}
            />
            <SectionEditor
              label="Fixed"
              value={form.sections.fixed}
              onChange={(v) => setForm((p) => ({ ...p, sections: { ...p.sections, fixed: v } }))}
            />
            <SectionEditor
              label="Removed"
              value={form.sections.removed}
              onChange={(v) => setForm((p) => ({ ...p, sections: { ...p.sections, removed: v } }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={saveForm} variant="contained" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
